// Regras de Taxa de Dificuldade de Entrega (FreteTda) — PO1-3066 + PO1-3085 / épico PO1-3044.
//
// Cada regra é por embarcador + transportadora, com ALCANCE: por CPF/CNPJ do destinatário, por
// cidade+UF, por UF sozinha, por faixa de CEP, ou pelo documento combinado com uma dessas
// geografias. Pelo menos um critério é obrigatório, e cidade e faixa de CEP são mutuamente
// exclusivas. O TIPO da regra (TDA, TDE, TRT, Descarga, Agendamento, Outro) vive no campo
// `codigo` — não há mais taxa-âncora em FreteTaxa.
//
// Endpoints do backend_hub (usar `api`):
//
//   GET    /cadastros/frete-tda/tipos                         → [{ codigo, nome }]
//   GET    /cadastros/frete-tda?idEmbarcador=&idTransportadora=&cnpj=&status=&search=
//                                                              → { items: Regra[] }
//   GET    /cadastros/frete-tda/:id                           → Regra
//   POST   /cadastros/frete-tda                               → 201 { id, aviso? }
//   PUT    /cadastros/frete-tda/:id                           → { ok, aviso? }
//   PATCH  /cadastros/frete-tda/:id/status { status }
//   POST   /cadastros/frete-tda/importar (multipart: file, idEmbarcador, idTransportadora, dryRun)
//          → { dryRun, linhasLidas, linhasValidas, warnings: [{ linha, motivo }], inseridos, ignorados }
//
// UF e cidade vêm dos endpoints que já existem: GET /frete/estados e
// GET /frete/estados/:idEstado/cidades.
//
// `aviso` é NÃO BLOQUEANTE: vem junto do 201/200 quando existe regra do mesmo tipo com alcance
// mais específico cobrindo o mesmo caso (a mais específica vence, então esta será ignorada lá).
//
// Regra = { id, idEmbarcador, idTransportadora, nomeTransportadora, cnpj, codigo, tipoRegra,
//           nome, descricao, tipo ("PERCENTUAL"|"FIXO"), por, valor, valorMinimo, status,
//           idEstado, idCidade, uf, cidade, cepInicial, cepFinal, alcance, creationTime }
import api from "./api";

import { digitsOnly } from "../utils/formatDoc";

export const FRETE_TDA_URL = "/cadastros/frete-tda";

// Opções de "Por" dependem do tipo de cálculo (mesma regra do calc-frete-service).
// FIXO/FRETE existe no legado (4.295 regras da International School x Alfa) e o cálculo trata
// como valor cheio, igual a FIXO/CTE e FIXO/NF.
export const FRETE_TDA_TIPO_POR = {
  PERCENTUAL: ["FRETE", "NF"],
  FIXO: ["CTE", "NF", "FRETE"],
};

// O rótulo depende do cálculo: no FIXO o valor é cheio, não uma fração da base.
export const FRETE_TDA_POR_LABEL = {
  PERCENTUAL: {
    FRETE: "% do frete (custo + taxas)",
    NF: "% do valor da NF",
  },
  FIXO: {
    CTE: "valor cheio por CT-e",
    NF: "valor cheio por NF",
    FRETE: "valor cheio somado ao frete",
  },
};

export function rotuloPor(tipo, por) {
  const doTipo = FRETE_TDA_POR_LABEL[tipo];
  return (doTipo && doTipo[por]) || por;
}

// Colunas esperadas na planilha de importação (.xlsx), na ordem.
export const FRETE_TDA_COLUNAS_PLANILHA = [
  // O cabeçalho continua "CNPJ" (é a chave que o importador do backend lê), mas aceita CPF.
  // OPCIONAL desde a PO1-3085: sem documento, a regra vale por praça (UF, cidade ou CEP).
  {
    coluna: "CNPJ",
    descricao:
      "opcional. CPF com 11 dígitos ou CNPJ com 14. Zeros à esquerda perdidos pelo Excel são "
      + "recuperados pelo sistema. Vazio = regra por praça, para qualquer destinatário.",
  },
  { coluna: "Nome", descricao: "nome da regra (ex.: TDE Soma Autopeças)" },
  { coluna: "Descricao", descricao: "opcional" },
  { coluna: "Tipo", descricao: "TDA, TDE, TRT, DESC (ou DESCARGA), AGE (ou AGENDAMENTO), OUTRO" },
  { coluna: "Calculo", descricao: "PERCENTUAL ou FIXO" },
  { coluna: "Por", descricao: "PERCENTUAL: FRETE ou NF; FIXO: CTE, NF ou FRETE" },
  { coluna: "Valor", descricao: "percentual ou valor fixo" },
  { coluna: "ValorMinimo", descricao: "opcional" },
  // Alcance (PO1-3085): todas opcionais, mas a linha precisa de PELO MENOS UM critério —
  // CNPJ, UF ou a faixa de CEP.
  { coluna: "UF", descricao: "opcional; sigla (ex.: SC). Sozinha, a regra vale em todo o estado" },
  { coluna: "Cidade", descricao: "opcional; exige UF. Não pode vir junto com faixa de CEP" },
  { coluna: "CepInicial", descricao: "opcional; 8 dígitos. Não pode vir junto com Cidade" },
  { coluna: "CepFinal", descricao: "obrigatória quando CepInicial vem preenchida" },
];

// Os 6 tipos de regra. Lista FIXA no cliente (o backend expõe a mesma em GET /tipos, mas não
// há por que pagar uma requisição por algo que não muda). O valor vai no campo `codigo`.
export const FRETE_TDA_TIPOS = [
  { codigo: "TDA", nome: "TDA — Taxa de Dificuldade de Acesso" },
  { codigo: "TDE", nome: "TDE — Taxa de Dificuldade de Entrega" },
  { codigo: "TRT", nome: "TRT — Taxa de Restrição de Trânsito" },
  { codigo: "DESC", nome: "Descarga" },
  { codigo: "AGE", nome: "Agendamento" },
  { codigo: "OUTRO", nome: "Outro" },
];

// Cor do badge por tipo, na listagem.
export const FRETE_TDA_TIPO_COR = {
  TDA: "info",
  TDE: "primary",
  TRT: "warning",
  DESC: "success",
  AGE: "secondary",
  OUTRO: "default",
};

// Tipo de uma regra pelo PREFIXO do código (as regras herdadas do legado têm códigos como
// "TDE1261460", "AGE97336", "DESC97322"). Mesma classificação do backend e do calc-frete.
export function tipoDoCodigo(codigo) {
  const c = String(codigo || "").trim().toUpperCase();
  if (!c) return "OUTRO";
  if (c.indexOf("TDA") === 0) return "TDA";
  if (c.indexOf("TDE") === 0) return "TDE";
  if (c.indexOf("TRT") === 0) return "TRT";
  if (c.indexOf("DESC") === 0) return "DESC";
  if (c.indexOf("AGE") === 0) return "AGE";
  return "OUTRO";
}

export function nomeDoTipo(codigo) {
  const tipo = tipoDoCodigo(codigo);
  const achado = FRETE_TDA_TIPOS.find((t) => t.codigo === tipo);
  return achado ? achado.nome : tipo;
}

// Rótulo do alcance de uma regra, para a coluna "Alcance" da listagem. Espelha o rotuloAlcance
// do backend e o _alcanceFreteTda do calc-frete.
export function rotuloAlcance(regra) {
  const r = regra || {};
  const partes = [];
  if (r.cnpj) partes.push(String(r.cnpj).length === 11 ? "CPF" : "CNPJ");
  if (r.cepInicial) partes.push(`CEP ${formatCep(r.cepInicial)}–${formatCep(r.cepFinal)}`);
  else if (r.idCidade) partes.push(r.uf && r.cidade ? `${r.uf}/${r.cidade}` : "cidade");
  else if (r.idEstado) partes.push(r.uf || "UF");
  return partes.length ? partes.join(" + ") : "sem restrição";
}

// CEP de int (89800000) para texto (89800-000).
export function formatCep(valor) {
  if (valor === null || valor === undefined || valor === "") return "";
  const d = String(valor).replace(/\D/g, "").padStart(8, "0");
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

export function getCepDigits(valor) {
  return String(valor === null || valor === undefined ? "" : valor).replace(/\D/g, "").slice(0, 8);
}

// Máscara progressiva enquanto digita.
export function formatCepInput(valor) {
  const d = getCepDigits(valor);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
}

export async function listarRegras(params) {
  const { data } = await api.get(FRETE_TDA_URL, { params });
  return data && Array.isArray(data.items) ? data.items : Array.isArray(data) ? data : [];
}

export async function obterRegra(id) {
  const { data } = await api.get(`${FRETE_TDA_URL}/${id}`);
  return data;
}

export async function criarRegra(body) {
  const { data } = await api.post(FRETE_TDA_URL, body);
  return data;
}

export async function atualizarRegra(id, body) {
  const { data } = await api.put(`${FRETE_TDA_URL}/${id}`, body);
  return data;
}

export async function alterarStatusRegra(id, status) {
  const { data } = await api.patch(`${FRETE_TDA_URL}/${id}/status`, { status });
  return data;
}

// Importação por planilha: dryRun=true só valida (pré-visualização), false grava.
export async function importarPlanilha({ file, idEmbarcador, idTransportadora, dryRun }) {
  const form = new FormData();
  form.append("file", file, file.name);
  form.append("idEmbarcador", String(idEmbarcador));
  form.append("idTransportadora", String(idTransportadora));
  form.append("dryRun", dryRun ? "true" : "false");
  const { data } = await api.post(`${FRETE_TDA_URL}/importar`, form);
  return data || {};
}

// Razão social pelo CNPJ (mesmo lookup do wizard de transportadora). Null se falhar ou se
// não for CNPJ (CPF não tem lookup).
export async function consultarRazaoSocial(cnpj) {
  const digits = digitsOnly(cnpj);
  if (digits.length !== 14) return null;
  try {
    const { data } = await api.get(`/frete/tabelas/cnpj-lookup/${digits}`);
    if (!data || typeof data !== "object") return null;
    return data.razao_social || data.razaoSocial || data.nome_fantasia || null;
  } catch (error) {
    return null;
  }
}

export function mensagemErroFreteTda(error, fallback) {
  const status = error && error.response && error.response.status;
  const payload = error && error.response && error.response.data;
  const doBackend =
    payload && typeof payload === "object"
      ? (typeof payload.error === "string" && payload.error) ||
        (typeof payload.message === "string" && payload.message) ||
        ""
      : typeof payload === "string"
        ? payload
        : "";

  if (status === 400) return doBackend || "Dados inválidos. Confira os campos.";
  if (status === 403) return doBackend || "Você não tem acesso a este embarcador.";
  if (status === 404) return doBackend || "Regra não encontrada no seu escopo.";
  if (status === 409) return doBackend || "Já existe uma regra igual para esta transportadora e CPF/CNPJ.";
  if (status === 413) return "Arquivo acima do limite aceito pelo servidor.";
  if (status === 503) return "Serviço indisponível no momento. Tente de novo em alguns minutos; se continuar, avise o suporte.";
  return doBackend || (error && error.message) || fallback || "Não foi possível concluir a operação.";
}
